import type * as React from 'react'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import {
  Dialog,
  Button,
  Flex,
  Heading,
  Text,
  CheckIcon,
  CloseIcon,
  IconButton,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { SEGMENT_NAMES } from '~publish/legacy/constants'
import { actions as modalActions } from '~publish/legacy/modals/reducer'

import {
  useAccount,
  useCanStartTrial,
} from '../../../../contexts/AccountContext'

import styles from './TrialCollaborationModal.module.css'

const onUpsellClick = ({
  dispatch,
  canStartTrial,
}: {
  dispatch: (action: unknown) => void
  canStartTrial: boolean
}): void => {
  const upgradePath = canStartTrial ? 'trial' : 'upgrade'
  const { MODALS, actions } = window?.appshell || {}
  actions.openModal(MODALS.planSelector, {
    cta: SEGMENT_NAMES.COMPOSER_REQUEST_APPROVAL_UPSELL,
    upgradePathName: `approvals-${upgradePath}`,
  })

  dispatch(modalActions.hideTrialCollaborationModal())
}

const TrialCollaborationModal: React.FC = () => {
  const dispatch = useDispatch()
  const user = useAccount()
  const canStartTrial = useCanStartTrial()
  const headingText = canStartTrial
    ? 'Try our collaboration features for free!'
    : 'Try collaboration features on the Team plan'

  const commonTrackingProps =
    user?.account?.currentOrganization?.commonTrackingProperties || null

  useEffect(() => {
    BufferTracker.pageViewed({
      name: 'Draft Paywall Modal',
      product: 'publish',
      cta: 'Drafts Request Approval',
      path: window.location.pathname || null,
    })

    const upgradePath = canStartTrial ? 'trial' : 'upgrade'

    BufferTracker.upgradePathViewed({
      organizationId: user?.account?.currentOrganization?.id || '',
      upgradePathName: `approvals-${upgradePath}`,
      product: 'publish',
      clientName: Client.PublishWeb,
    })
  }, [user, commonTrackingProps, canStartTrial])

  const handleStartStrialClick = (): void => {
    const { MODALS, actions } = window?.appshell || {}
    dispatch(modalActions.hideTrialCollaborationModal())
    actions.openModal(MODALS.startTrial, {
      ctaView: 'trialCollaborationModal',
      ctaLocation: 'draft',
      cta: 'startTrial',
      ctaButton: 'startTrial',
      upgradePathName: 'approvals-trial',
    })
  }

  return (
    <Dialog
      open={true}
      onOpenChange={() => dispatch(modalActions.hideTrialCollaborationModal())}
    >
      <Dialog.Content size="large" className={styles.dialogContent}>
        <Dialog.Body className={styles.dialogBody}>
          <Flex
            direction="row"
            justify="end"
            className={styles.closeButtonContainer}
          >
            <IconButton
              variant="tertiary"
              label="Close modal"
              onClick={() =>
                dispatch(modalActions.hideTrialCollaborationModal())
              }
              className={styles.closeButton}
            >
              <CloseIcon />
            </IconButton>
          </Flex>

          <Flex direction="column" gap="lg" className={styles.contentContainer}>
            <Heading size="large" className={styles.heading}>
              {headingText}
            </Heading>
            <Text color="subtle">
              Send drafts for approval or review other&apos;s drafts plus...
            </Text>

            <Flex direction="column" gap="xs">
              <Flex direction="row" align="center" gap="xs">
                <CheckIcon color="brand" />
                <Text color="subtle">
                  Unlocked publishing, analytics and engagement
                </Text>
              </Flex>
              <Flex direction="row" align="center" gap="xs">
                <CheckIcon color="brand" />
                <Text color="subtle">Unlimited seats for your team</Text>
              </Flex>
              <Flex direction="row" align="center" gap="xs">
                <CheckIcon color="brand" />
                <Text color="subtle">Easy, clear exportable reports</Text>
              </Flex>
            </Flex>
          </Flex>

          {canStartTrial && (
            <Flex
              direction="row"
              align="center"
              gap="md"
              className={styles.buttonContainer}
            >
              <Button
                id="collab-start-trial-cta"
                variant="primary"
                onClick={handleStartStrialClick}
              >
                Start a 14-day Free Trial
              </Button>
              <Button
                variant="secondary"
                onClick={() => onUpsellClick({ dispatch, canStartTrial })}
              >
                See Paid Plans
              </Button>
            </Flex>
          )}
          {!canStartTrial && (
            <Button
              variant="primary"
              onClick={() => onUpsellClick({ dispatch, canStartTrial })}
            >
              Discover the Team plan
            </Button>
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}

export default TrialCollaborationModal
